import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

const parsePrice = (price) => Number(String(price || "").replace(/[^\d.]/g, "")) || 0;

const formatEur = (value) => `€ ${value.toFixed(2)}`;

const OrderSummaryTable = ({ items }) => {
    const { t } = useTranslation();
    const subtotal = items.reduce((sum, item) => sum + parsePrice(item.price), 0);

    return (
        <div className="mt-10">
            <p className="text-sm font-semibold text-slate-900">
                {t("GIFT_ORDER_TOTALS_ITEMS")} <span className="ml-1 text-slate-400">{items.length}</span>
            </p>

            <div className="mt-4 hidden divide-y divide-slate-200 border-b border-slate-200 md:block">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className="grid grid-cols-[64px_2.2fr_0.9fr_0.6fr_0.8fr] items-stretch gap-4 py-4"
                    >
                        <div className="flex items-center">
                            <img
                                src={item.image}
                                alt=""
                                className="h-12 w-12 rounded-xl border border-slate-200 bg-white object-contain"
                            />
                        </div>
                        <div className="flex flex-col justify-center border-r border-slate-200">
                            <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                            {item.link ? (
                                <a href={item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-4 hover:opacity-75">
                                    {t("COMMON_SEE_ONLINE")}
                                    <ArrowRight className="h-3 w-3" />
                                </a>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-4">
                                    {t("COMMON_SEE_ONLINE")}
                                    <ArrowRight className="h-3 w-3" />
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col items-center justify-center gap-0.5 border-r border-slate-200">
                            <p className="text-[11px] text-slate-400">{t("COMMON_CODE")}</p>
                            <p className="text-xs text-slate-600">{item.code}</p>
                        </div>
                        <div className="flex flex-col items-center justify-center gap-0.5 border-r border-slate-200">
                            <p className="text-[11px] text-slate-400">{t("COMMON_QTY")}</p>
                            <p className="text-xs text-slate-600">{item.qty}</p>
                        </div>
                        <div className="flex flex-col items-center justify-center gap-0.5">
                            <p className="text-[11px] text-slate-400">{t("COMMON_GIFT_VALUE")}</p>
                            <p className="text-xs font-semibold text-slate-700">{item.price}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 space-y-3 md:hidden">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-[0_10px_20px_rgba(15,23,42,0.04)]"
                    >
                        <div className="flex items-start gap-3">
                            <img
                                src={item.image}
                                alt=""
                                className="h-12 w-12 rounded-xl border border-slate-200 bg-white object-contain"
                            />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                                {item.link ? (
                                    <a href={item.link} target="_blank" rel="noreferrer" className="text-xs text-primary underline underline-offset-4 hover:opacity-75">{t("COMMON_SEE_ONLINE")}</a>
                                ) : (
                                    <span className="text-xs text-primary underline underline-offset-4">{t("COMMON_SEE_ONLINE")}</span>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-4 text-xs text-slate-500">
                            <div>
                                <p className="text-[11px] text-slate-400">{t("COMMON_CODE")}</p>
                                <p>{item.code}</p>
                            </div>
                            <div>
                                <p className="text-[11px] text-slate-400">{t("COMMON_QTY")}</p>
                                <p>{item.qty}</p>
                            </div>
                            <div>
                                <p className="text-[11px] text-slate-400">{t("COMMON_GIFT_VALUE")}</p>
                                <p className="text-xs font-semibold text-slate-700">{item.price}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8">
                <p className="text-sm font-semibold text-slate-900">{t("GIFT_ORDER_TOTALS_TITLE")}</p>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                        <span>{t("GIFT_ORDER_TOTALS_ITEMS_PIECE")}</span>
                        <span>{items.length} {t("COMMON_PRODUCTS")}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                        <span>{t("COMMON_SUBTOTAL")}</span>
                        <span>{formatEur(subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                        <span>{t("COMMON_SHIPPING")}</span>
                        <span className="font-medium text-primary">{t("COMMON_FREE")}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                        <span className="text-green-600">{t("COMMON_DISCOUNT")}</span>
                        <span className="font-medium text-green-600">100%</span>
                    </div>
                    <div className="flex items-center justify-between font-semibold text-slate-900">
                        <span>{t("GIFT_ORDER_TOTALS_TOTAL_COST")}</span>
                        <span>{formatEur(0.00)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSummaryTable;
